import { useSuspenseQuery } from '@tanstack/react-query';
import type { TypeGetRecruitNoticesWorkLocationsResponse } from '~/api/recruitNotices';
import { registerMockAPI } from '~/mocks/mockApis';

import { axiosInstance } from '~/services/fetchServiceV2';

export const WORK_LOCATIONS_QUERY_KEY = 'work-locations';

/** @description 모집중인 근무지 목록 조회 */
export const useGetWorkLocations = () => {
  const { data } = useSuspenseQuery({
    queryKey: [WORK_LOCATIONS_QUERY_KEY],
    queryFn: async () => {
      const endpoint = '/recruit-notices/work-locations';
      return await axiosInstance.get<TypeGetRecruitNoticesWorkLocationsResponse>(endpoint);
    },
    gcTime: 0,
    retry: 1,
    select: (response) => response.data.data ?? [],
  });

  return { data };
};

registerMockAPI<TypeGetRecruitNoticesWorkLocationsResponse>({
  method: 'get',
  endpoint: '/recruit-notices/work-locations',
  label: '근무지 선택/근무지 목록 조회',
  scenarios: [
    {
      name: '근무지 목록 있음 (3개)',
      response: {
        code: '200',
        message: 'OK',
        data: [
          {
            workLocationId: 1,
            name: '김포',
            roadAddress: '경기도 김포시 고촌읍 아라육로75, 컬리 김포켄달스퀘어',
            lotAddress: '경기도 김포시 고촌읍 전호리 산25',
            latitude: 37.6284,
            longitude: 126.7154,
            shuttleAvailable: true,
            parkingAvailable: false,
            parkingGuide: null,
            locationUrl: 'https://example.com/location/1',
          },
          {
            workLocationId: 2,
            name: '안산',
            roadAddress: '경기 안산시 단원구 해봉로 232 (원곡동 756-11)',
            lotAddress: '경기 안산시 단원구 원곡동 756-11',
            latitude: 37.3219,
            longitude: 126.8309,
            shuttleAvailable: false,
            parkingAvailable: true,
            parkingGuide: 'https://example.com/parking/2',
            locationUrl: 'https://example.com/location/2',
          },
          {
            workLocationId: 3,
            name: '원창',
            roadAddress: '인천시 서구 원창동 394-15, 로지스포트물류센터 7층',
            lotAddress: '인천시 서구 원창동 394-15',
            latitude: 37.5469,
            longitude: 126.6782,
            shuttleAvailable: true,
            parkingAvailable: true,
            parkingGuide: 'https://example.com/parking/3',
            locationUrl: 'https://example.com/location/3',
          },
        ],
      },
    },
    {
      name: '근무지 많음 (8개)',
      response: {
        code: '200',
        message: 'OK',
        data: [
          {
            workLocationId: 1,
            name: '김포',
            roadAddress: '경기도 김포시 고촌읍 아라육로75, 컬리 김포켄달스퀘어',
            lotAddress: '경기도 김포시 고촌읍 전호리 산25',
            latitude: 37.6284,
            longitude: 126.7154,
            shuttleAvailable: true,
            parkingAvailable: false,
            parkingGuide: null,
            locationUrl: 'https://example.com/location/1',
          },
          {
            workLocationId: 2,
            name: '안산',
            roadAddress: '경기 안산시 단원구 해봉로 232 (원곡동 756-11)',
            lotAddress: '경기 안산시 단원구 원곡동 756-11',
            latitude: 37.3219,
            longitude: 126.8309,
            shuttleAvailable: false,
            parkingAvailable: true,
            parkingGuide: 'https://example.com/parking/2',
            locationUrl: 'https://example.com/location/2',
          },
          {
            workLocationId: 3,
            name: '원창',
            roadAddress: '인천시 서구 원창동 394-15, 로지스포트물류센터 7층',
            lotAddress: '인천시 서구 원창동 394-15',
            latitude: 37.5469,
            longitude: 126.6782,
            shuttleAvailable: true,
            parkingAvailable: true,
            parkingGuide: 'https://example.com/parking/3',
            locationUrl: 'https://example.com/location/3',
          },
          {
            workLocationId: 4,
            name: '평택',
            roadAddress: '경기도 평택시 포승읍 평택항로 200',
            lotAddress: '경기도 평택시 포승읍 평택항로 200',
            latitude: 37.0,
            longitude: 127.0,
            shuttleAvailable: true,
            parkingAvailable: true,
            parkingGuide: 'https://example.com/parking/4',
            locationUrl: 'https://example.com/location/4',
          },
          {
            workLocationId: 5,
            name: '봉천',
            roadAddress: '서울특별시 관악구 봉천로 123',
            lotAddress: '서울특별시 관악구 봉천로 123',
            latitude: 37.5,
            longitude: 126.5,
            shuttleAvailable: false,
            parkingAvailable: false,
            parkingGuide: null,
            locationUrl: 'https://example.com/location/5',
          },
          {
            workLocationId: 6,
            name: '역삼',
            roadAddress: '서울특별시 강남구 테헤란로 123',
            lotAddress: '서울특별시 강남구 테헤란로 123',
            latitude: 37.6,
            longitude: 127.2,
            shuttleAvailable: true,
            parkingAvailable: true,
            parkingGuide: 'https://example.com/parking/6',
            locationUrl: 'https://example.com/location/6',
          },
          {
            workLocationId: 7,
            name: '포항',
            roadAddress: '경상북도 포항시 남구 동해안로 123',
            lotAddress: '경상북도 포항시 남구 동해안로 123',
            latitude: 37.2,
            longitude: 127.3,
            shuttleAvailable: false,
            parkingAvailable: true,
            parkingGuide: 'https://example.com/parking/7',
            locationUrl: 'https://example.com/location/7',
          },
          {
            workLocationId: 8,
            name: '제주도',
            roadAddress: '제주특별자치도 제주시 조천읍 함덕로 123',
            lotAddress: '제주특별자치도 제주시 조천읍 함덕로 123',
            latitude: 37.1,
            longitude: 127.4,
            shuttleAvailable: true,
            parkingAvailable: false,
            parkingGuide: null,
            locationUrl: 'https://example.com/location/8',
          },
        ],
      },
    },
    {
      name: '근무지 없음',
      response: {
        code: '200',
        message: 'OK',
        data: [],
      },
    },
  ],
});
